import { useNavigate } from '@tanstack/react-router';
import { memo } from 'react';

import { merge } from '@/components';
import { Button, Flex, Heading, ProgressBar, Text } from '@/components/core';
import { LightningIcon } from '@/components/icons';
import { useRemainingDaysOfTrial, useRudderAnalytics } from '@/hooks';
import { toOrganizationId, toPlanId, useGetOrganization, useGetPlan } from '@/hooks/api';
import { DivProps } from '@/utils';

export type UpgradeCardProps = DivProps & {
  organizationId: string;
};

const IUpgradeCard = ({ organizationId, className, ...rest }: UpgradeCardProps) => {
  const { data: organization } = useGetOrganization(toOrganizationId(organizationId));
  const { data: plan } = useGetPlan(toPlanId(organization?.planId));
  const navigate = useNavigate();
  const { track } = useRudderAnalytics();

  const { daysRemaining, trialDays } = useRemainingDaysOfTrial();

  if (!organization || !plan) return null;

  const variant = (daysRemaining ?? 0) < 3 ? 'error' : 'primary';

  return (
    <Flex
      col
      variant="secondary"
      className={merge(
        'gap-md m-2 rounded-xl border-1 border-gray-200 p-4 shadow-sm',
        variant == 'error' && 'from-error-100 bg-gradient-to-r to-white',
        className
      )}
      {...rest}
    >
      <Flex variant="plain" className="gap-xs items-center">
        <LightningIcon />
        <Heading className="grow">Free trial</Heading>
        <Text>{daysRemaining} days left</Text>
      </Flex>

      <ProgressBar
        variant={variant}
        aria-label="free-plan-limit"
        value={(trialDays ?? 0) - (daysRemaining ?? 0)}
        maxValue={trialDays}
      >
        {() => (
          <Flex variant="plain">
            <Text className="grow">All the team plan features for free.</Text>
          </Flex>
        )}
      </ProgressBar>
      <Button onPress={track('Upgrade Clicked', () => navigate({ to: '/settings/plans' }))} variant="secondary">
        <Text>Upgrade now</Text>
      </Button>
    </Flex>
  );
};

export const UpgradeCard = memo(IUpgradeCard) as typeof IUpgradeCard;
